import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeftRight, Play, RotateCcw, X } from 'lucide-react';
import {
  ROLE_LABEL,
  ROLE_SAMPLE,
  ROLE_SAMPLE_HASH,
  VOICES,
  VOICE_LABEL,
  VOICE_ROLES,
  audioFilename,
  useVoicePrefs,
  type VoiceId,
  type VoiceRole,
} from '../state/Voices';
import { useAudio } from '../audio/useAudio';
import { ThemePicker } from './ThemePicker';
import { vars } from '../styles/theme.css';
import { text } from '../styles/text.css';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function SettingsModal({ open, onClose }: Props) {
  const { prefs, setVoice, swap, reset } = useVoicePrefs();
  const { play, isPlaying } = useAudio();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const playSample = (role: VoiceRole, voice: VoiceId) => {
    play(
      `sample:${role}:${voice}`,
      audioFilename(ROLE_SAMPLE_HASH[role], voice),
    );
  };

  // Portal the modal to <body> so its fixed positioning is relative to the
  // viewport. Without this, an ancestor that creates a containing block (e.g.
  // the sticky header uses backdrop-filter, which counts) would anchor the
  // modal inside the header's bounds — not what we want.
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      />
      {/* Panel — pinned to bottom on mobile (bottom-sheet), centered on desktop. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        onClick={(e) => e.stopPropagation()}
        className="fixed z-50 left-0 right-0 bottom-0 mx-auto sm:left-1/2 sm:right-auto sm:bottom-auto sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2"
        style={{
          width: '100%',
          maxWidth: '28rem',
          backgroundColor: vars.color.surface,
          border: `1px solid ${vars.color.border}`,
          borderRadius: 16,
          padding: 20,
          maxHeight: '85dvh',
          overflowY: 'auto',
        }}
      >
        <header className="flex items-center justify-between mb-4">
          <h2
            id="settings-title"
            className={text.h2}
            style={{ color: vars.color.ink }}
          >
            Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md transition-colors"
            style={{ color: vars.color.inkSoft }}
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        {/* Theme */}
        <section className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <h3
              className={text.h3}
              style={{ color: vars.color.ink, marginBottom: 0 }}
            >
              Theme
            </h3>
            <ThemePicker />
          </div>
        </section>

        {/* Voices */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3
              className={text.h3}
              style={{ color: vars.color.ink, marginBottom: 0 }}
            >
              Voices
            </h3>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={swap}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors ${text.caption}`}
                style={{ color: vars.color.inkSoft, fontWeight: 600 }}
                aria-label="Swap Staff and You voices"
              >
                <ArrowLeftRight size={13} strokeWidth={2} />
                Swap
              </button>
              <button
                type="button"
                onClick={reset}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors ${text.caption}`}
                style={{ color: vars.color.inkMuted, fontWeight: 600 }}
                aria-label="Reset to defaults"
              >
                <RotateCcw size={13} strokeWidth={2} />
                Reset
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {VOICE_ROLES.map((role) => (
              <RoleRow
                key={role}
                role={role}
                activeVoice={prefs[role]}
                onPick={(v) => setVoice(role, v)}
                onPlay={(v) => playSample(role, v)}
                isPlaying={(v) => isPlaying(`sample:${role}:${v}`)}
              />
            ))}
          </div>

          <p
            className={text.caption}
            style={{ color: vars.color.inkMuted, marginTop: 12 }}
          >
            Tap a voice to select. Tap the play icon to preview with a sample
            phrase for that role.
          </p>
        </section>
      </div>
    </>,
    document.body,
  );
}

function RoleRow({
  role,
  activeVoice,
  onPick,
  onPlay,
  isPlaying,
}: {
  role: VoiceRole;
  activeVoice: VoiceId;
  onPick: (v: VoiceId) => void;
  onPlay: (v: VoiceId) => void;
  isPlaying: (v: VoiceId) => boolean;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <h3
          className={text.h3}
          style={{ color: vars.color.ink, marginBottom: 0 }}
        >
          {ROLE_LABEL[role]}
        </h3>
        <span
          className={`font-jp ${text.caption}`}
          style={{ color: vars.color.inkMuted }}
        >
          {ROLE_SAMPLE[role]}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {VOICES.map((v) => {
          const active = v === activeVoice;
          const playing = isPlaying(v);
          return (
            <div
              key={v}
              className="flex items-stretch overflow-hidden"
              style={{
                border: `1px solid ${active ? vars.color.accent : vars.color.border}`,
                borderRadius: 10,
                backgroundColor: active ? vars.color.bubbleYou : 'transparent',
              }}
            >
              <button
                type="button"
                onClick={() => onPick(v)}
                className={`flex-1 px-2 py-2 text-left transition-colors ${text.meta}`}
                style={{
                  color: active ? vars.color.ink : vars.color.inkSoft,
                  fontWeight: active ? 600 : 500,
                }}
                aria-pressed={active}
                aria-label={`Use ${VOICE_LABEL[v]} for ${ROLE_LABEL[role]}`}
              >
                {VOICE_LABEL[v]}
              </button>
              <button
                type="button"
                onClick={() => onPlay(v)}
                className="flex items-center justify-center transition-colors"
                style={{
                  width: 30,
                  borderLeft: `1px solid ${active ? vars.color.accent : vars.color.border}`,
                  color: playing ? vars.color.accent : vars.color.inkMuted,
                }}
                aria-label={`Preview ${VOICE_LABEL[v]} as ${ROLE_LABEL[role]}`}
              >
                <Play size={12} strokeWidth={2} fill={playing ? 'currentColor' : 'none'} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
